const MAX_DEPTH = 8;
const MAX_STRING_LENGTH = 10000;
const MAX_ARRAY_LENGTH = 100;

const TRUNCATED = '<truncated>';
const UNREADABLE = '<unreadable>';
const CIRCULAR = '<circular>';
const DROPPED = '<dropped>';
const REDACTED = '<redacted>';
const EMAIL_MARK = '<email>';
const PHONE_MARK = '<phone>';
const TOKEN_MARK = '<token>';

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const PLUS_E164_PATTERN = /\+[1-9]\d{9,14}\b/g;
const JWT_PATTERN =
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/-]{16,}={0,2}/gi;

// Non-global twins: a /g regex keeps lastIndex, so .test() alternates across calls.
const EMAIL_ONCE = /^[\w.+-]+@[\w-]+(?:\.[\w-]+)+$/;
const E164_ONCE = /^\+?[1-9]\d{9,14}$/;
// Capped: 'ProvisionedThroughputExceededException' is 37 chars, a Google refresh token 47+.
const STRUCTURAL_VALUE = /^[A-Za-z0-9_.:@/-]{1,40}$/;
// Past the cap only for prefixes no secret carries: an ARN and a Google resource name are
// both longer than a refresh token and are the identifiers an incident is traced by.
const STRUCTURAL_LONG =
  /^(?:arn:|spaces\/|users\/|projects\/)[A-Za-z0-9_.:@/-]{1,200}$/;
// An already-redacted value keeps its type marker instead of decaying to <redacted>.
const ALREADY_REDACTED = /^<[a-z]+>$/;

export const ALWAYS_REDACT: ReadonlySet<string> = new Set([
  'email',
  'emailaddress',
  'gmailaddress',
  'accountkey',
  'inviteeemail',
  'useremail',
  'fromemail',
  'calendaremail',
  'recipientemail',
  'toaddress',
  'phone',
  'phonenumber',
  'waid',
  'displayphonenumber',
  'msisdn',
  'to',
  'recipient',
  'principalid',
  'cardnumber',
  'pan',
  'creditcard',
  'displayname',
  'firstname',
  'lastname',
  'username',
  'sendername',
  'subject',
  'messagetext',
  'snippet',
  'textashtml',
  'prompt',
  'refinedquery',
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'sessiontoken',
  'authorizationcode',
  'apikey',
  'authorization',
  'clientsecret',
  'client_secret',
  'privatekey',
  'nonce',
  'signature',
  'password',
  'secret',
  'instructiontext',
  'decisionsummary',
  'counterpartlabel',
  'repositoryslug',
  'bodytext',
  'drafttext',
  'draftinput',
  'draftbody',
  'matchedrule',
  'evidencenotes',
]);

// PCI DSS 3.3.1: sensitive authentication data admits no representation in a log.
export const NEVER_REPRESENT: ReadonlySet<string> = new Set([
  'cvv',
  'cvc',
  'cvv2',
  'cav2',
  'cid',
  'pin',
  'pinblock',
  'track',
  'trackdata',
]);

// The audit trail SOC 2 CC7.2 and CIS 8.5 require; checked before ALWAYS_REDACT.
export const NEVER_REDACT: ReadonlySet<string> = new Set([
  'userid',
  'teamid',
  'messageid',
  'internetmessageid',
  'inboxentryid',
  'doboxid',
  'conversationid',
  'agentid',
  'phonenumberid',
  'bsuid',
  'igaccountid',
  'correlationid',
  'sourceip',
  'ip',
  'outcome',
  'timestamp',
  'httpstatus',
  'requestid',
]);

// 'message' and 'code' stay out — both are diagnostic here, and a bare OAuth 'code' is not
// separable from an AWS error code by name or by shape.
export const AMBIGUOUS_KEYS: ReadonlySet<string> = new Set([
  'name',
  'title',
  'content',
  'query',
  'goal',
  'body',
  'text',
  'reason',
  'description',
  'slots',
  'missinginfo',
]);

// stacktrace.frames is oldest-first: a head-slice would drop the crash site.
const UNCAPPED_ARRAY_KEYS: ReadonlySet<string> = new Set(['frames']);

function truncate(input: string): string {
  return input.length > MAX_STRING_LENGTH
    ? `${input.slice(0, MAX_STRING_LENGTH)}${TRUNCATED}`
    : input;
}

function neutralizeNewlines(input: string): string {
  return input.replace(/\r/g, '\\r').replace(/\n/g, '\\n');
}

function redactPatterns(input: string): string {
  return input
    .replace(JWT_PATTERN, TOKEN_MARK)
    .replace(BEARER_PATTERN, TOKEN_MARK)
    .replace(EMAIL_PATTERN, EMAIL_MARK)
    .replace(PLUS_E164_PATTERN, PHONE_MARK);
}

function redactString(input: string): string {
  // Detect first, truncate second: truncating first can slice an email in half, leaving
  // the local part readable and the domain gone — more identifying, not less.
  return truncate(neutralizeNewlines(redactPatterns(input)));
}

// A deliberately formatted block: the patterns run and the newlines survive. Still capped —
// Slack rejects a payload over 40k and alertChannel swallows the rejection.
export function redactBlockText(input: string): string {
  return truncate(redactPatterns(String(input)));
}

// Walked here rather than by the walker, so it carries neither a depth counter nor a cycle
// guard: cap the length and refuse to descend into a nested node instead of recursing.
function redactKeyedArray(key: string, value: unknown[]): unknown[] {
  const capped = value.slice(0, MAX_ARRAY_LENGTH).map((item) => {
    if (item === null || item === undefined || typeof item === 'boolean') {
      return item;
    }
    if (typeof item === 'object') {
      return REDACTED;
    }
    const redacted = redactByKey(key, item);
    if (redacted !== undefined) {
      return redacted;
    }
    // What the walker would have done with a leaf no key rule claimed.
    return typeof item === 'string' ? redactString(item) : item;
  });
  return value.length > MAX_ARRAY_LENGTH ? [...capped, TRUNCATED] : capped;
}

function redactByKey(key: string, value: unknown): unknown {
  const normalized = key.toLowerCase();

  if (NEVER_REPRESENT.has(normalized)) {
    return DROPPED;
  }

  // The audit value survives, but not its power to forge a log line — and an object under
  // an audit key still holds children the key rules have to see.
  if (NEVER_REDACT.has(normalized)) {
    return typeof value === 'string'
      ? truncate(neutralizeNewlines(value))
      : undefined;
  }

  if (ALWAYS_REDACT.has(normalized)) {
    // A boolean cannot identify anyone; every other non-string can — a waId and a card
    // number both arrive as numbers, and neither has a shape the value pass matches.
    if (value === null || value === undefined || typeof value === 'boolean') {
      return value;
    }
    if (Array.isArray(value)) {
      return redactKeyedArray(key, value);
    }
    if (typeof value !== 'string') {
      return REDACTED;
    }
    const trimmed = value.trim();
    if (ALREADY_REDACTED.test(trimmed)) {
      return value;
    }
    if (E164_ONCE.test(trimmed)) {
      return PHONE_MARK;
    }
    if (EMAIL_ONCE.test(trimmed)) {
      return EMAIL_MARK;
    }
    return REDACTED;
  }

  if (AMBIGUOUS_KEYS.has(normalized)) {
    if (Array.isArray(value)) {
      return redactKeyedArray(key, value);
    }
    if (typeof value === 'string') {
      return STRUCTURAL_VALUE.test(value) || STRUCTURAL_LONG.test(value)
        ? redactString(value)
        : REDACTED;
    }
    // An object falls through to the walker on purpose: inside it the governing key is its
    // own, and 'name' says nothing about what a child called 'v' holds.
  }

  return undefined;
}

interface WalkState {
  seen: WeakSet<object>;
  // A node reached from two places was walked twice once the cycle guard became
  // path-scoped; caching makes a shared subtree cost one walk again.
  done: WeakMap<object, { depth: number; result: unknown; uncapped: boolean }>;
  circularHits: number;
}

// Assignment would let a literal __proto__ key mutate the accumulator's prototype and
// vanish from the output, taking the field with it.
function setKey(
  output: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  Object.defineProperty(output, key, {
    value,
    enumerable: true,
    writable: true,
    configurable: true,
  });
}

function redactError(error: Error, depth: number, state: WalkState): unknown {
  // Only name/message/stack are non-enumerable, so a plain spread loses statusCode, code,
  // $metadata, graphQLErrors and networkError — the fields that separate a throttle from a
  // permanent failure.
  const output: Record<string, unknown> = {
    name: error.name,
    message: redactString(error.message),
  };
  if (error.stack) {
    // Newlines are what makes a stack readable, and its frames are runtime-generated.
    output['stack'] = truncate(redactPatterns(error.stack));
  }
  for (const key of Object.keys(error)) {
    if (key in output) {
      continue;
    }
    try {
      const entry = (error as unknown as Record<string, unknown>)[key];
      const byKey = redactByKey(key, entry);
      setKey(
        output,
        key,
        byKey === undefined ? walk(entry, depth + 1, state) : byKey,
      );
    } catch {
      setKey(output, key, UNREADABLE);
    }
  }
  return output;
}

function walkObject(
  value: object,
  depth: number,
  state: WalkState,
  uncapped: boolean,
): unknown {
  if (Array.isArray(value)) {
    const capped = uncapped ? value : value.slice(0, MAX_ARRAY_LENGTH);
    const walked = capped.map((item) => walk(item, depth + 1, state));
    return !uncapped && value.length > MAX_ARRAY_LENGTH
      ? [...walked, TRUNCATED]
      : walked;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
    return `<binary:${value.byteLength} bytes>`;
  }

  if (value instanceof Map) {
    return walk(Object.fromEntries(value), depth, state);
  }

  if (value instanceof Set) {
    return walk([...value], depth, state);
  }

  if (value instanceof Error) {
    return redactError(value, depth, state);
  }

  const output: Record<string, unknown> = {};
  for (const key of Object.keys(value as Record<string, unknown>)) {
    try {
      const entry = (value as Record<string, unknown>)[key];
      const byKey = redactByKey(key, entry);
      const redacted =
        byKey === undefined
          ? walk(
              entry,
              depth + 1,
              state,
              UNCAPPED_ARRAY_KEYS.has(key.toLowerCase()),
            )
          : byKey;
      setKey(output, key, redacted);
    } catch {
      // One throwing getter must not erase its siblings.
      setKey(output, key, UNREADABLE);
    }
  }
  return output;
}

function walk(
  value: unknown,
  depth: number,
  state: WalkState,
  uncapped = false,
): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return redactString(value);
  }

  if (typeof value !== 'object') {
    return value;
  }

  if (depth >= MAX_DEPTH) {
    return TRUNCATED;
  }

  const node = value as object;

  // Only a shallower-or-equal entry is reusable: a deeper walk may have hit MAX_DEPTH.
  const cached = state.done.get(node);
  if (cached && cached.uncapped === uncapped && cached.depth <= depth) {
    return cached.result;
  }

  // Scoped to the current path, not the whole walk: a node referenced twice in a DAG —
  // `{ messages, firstMessage: messages[0] }` — is not a cycle.
  if (state.seen.has(node)) {
    state.circularHits += 1;
    return CIRCULAR;
  }
  state.seen.add(node);

  const circularBefore = state.circularHits;
  try {
    const result = walkObject(node, depth, state, uncapped);
    // A subtree that hit a cycle describes this path only.
    if (state.circularHits === circularBefore) {
      state.done.set(node, { depth, result, uncapped });
    }
    return result;
  } catch {
    // A throwing getter must not take down the log call that touched it.
    return UNREADABLE;
  } finally {
    state.seen.delete(node);
  }
}

export function redactPii(value: unknown): unknown {
  try {
    return walk(value, 0, {
      seen: new WeakSet(),
      done: new WeakMap(),
      circularHits: 0,
    });
  } catch {
    return UNREADABLE;
  }
}

// Marks a payload the caller already redacted, so the loglevel wrap passes it through.
class PreRedacted {
  constructor(private readonly value: string) {}

  toString(): string {
    return this.value;
  }

  // Without this a logger that never installed the wrap prints the marker, not the entry.
  [Symbol.for('nodejs.util.inspect.custom')](): string {
    return this.value;
  }
}

export function redactedJson(value: unknown): unknown {
  try {
    return new PreRedacted(JSON.stringify(redactPii(value)));
  } catch {
    // A BigInt or a throwing toJSON must not escape into the handler's catch block.
    return new PreRedacted(`{"redaction":"${UNREADABLE}"}`);
  }
}

// For a deliberately formatted multi-line block: PII patterns still run, newlines survive.
export function redactedBlock(text: string): unknown {
  try {
    return new PreRedacted(redactBlockText(text));
  } catch {
    return new PreRedacted(UNREADABLE);
  }
}

const INSTALLED_FLAG = '__piiRedactionInstalled';

type LogMethodName = 'trace' | 'debug' | 'info' | 'warn' | 'error';
type LogLevelNumber = 0 | 1 | 2 | 3 | 4 | 5;
type LogMethod = (...args: unknown[]) => void;

export interface RedactableLogger {
  methodFactory: (
    methodName: LogMethodName,
    level: LogLevelNumber,
    loggerName: string | symbol,
  ) => LogMethod;
  rebuild: () => void;
}

// setLogLevel is called at module scope by 204 files; without the flag each call would
// wrap the previous wrap, nesting the walker once per import.
export function installLogRedaction(logger: RedactableLogger): void {
  const flagged = logger as RedactableLogger & { [INSTALLED_FLAG]?: boolean };
  if (flagged[INSTALLED_FLAG]) {
    return;
  }

  // setLogLevel is untyped and 252 test files hand it a loglevel mock; a stand-in that
  // cannot rebuild cannot carry the wrap either.
  if (
    typeof logger?.methodFactory !== 'function' ||
    typeof logger?.rebuild !== 'function'
  ) {
    return;
  }

  const originalFactory = logger.methodFactory;
  logger.methodFactory = (methodName, level, loggerName) => {
    const rawMethod = originalFactory(methodName, level, loggerName);
    return (...args: unknown[]) =>
      rawMethod(
        ...args.map((arg) =>
          arg instanceof PreRedacted ? arg.toString() : redactPii(arg),
        ),
      );
  };

  flagged[INSTALLED_FLAG] = true;
  logger.rebuild();
}
